import React, { useState, useEffect, useRef } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import Pagination from '../../../../components/Pagination/Pagination';
import './ViewPinCode.css';

/* ─────────────── Share Pin Modal ─────────────── */
const SharePinModal = ({ pin, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ loading: false, error: '', success: '' });
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    // Lock scroll
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailValid) {
      setStatus({ loading: false, error: 'Please enter a valid email address.', success: '' });
      return;
    }

    setStatus({ loading: true, error: '', success: '' });
    try {
      const res = await apiFetch('/api/pin/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin_id: pin.id, email: email.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ loading: false, error: '', success: `✓ Pin sent to @${data.recipientUsername}` });
        onSuccess(pin.id);
      } else {
        setStatus({ loading: false, error: data.message || 'Something went wrong.', success: '' });
      }
    } catch {
      setStatus({ loading: false, error: 'Server error. Please try again.', success: '' });
    }
  };

  return (
    <div className="vp-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vp-modal-card">
        {/* Header */}
        <div className="vp-modal-header">
          <div className="vp-modal-header__icon">🔗</div>
          <div>
            <h3 className="vp-modal-header__title">Share E-Pin</h3>
            <p className="vp-modal-header__sub">Transfer this pin to another registered user</p>
          </div>
          <button className="vp-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Pin Being Shared */}
        <div className="vp-modal-pin-preview">
          <span className="vp-modal-pin-preview__label">Pin to transfer:</span>
          <code className="vp-modal-pin-preview__code">{pin.code}</code>
          {pin.expiresAt && (
            <span className="vp-modal-pin-preview__exp">Expires: {pin.expiresAt}</span>
          )}
        </div>

        {status.success ? (
          /* ── Success State ── */
          <div className="vp-modal-success">
            <div className="vp-modal-success__icon">✅</div>
            <div className="vp-modal-success__text">{status.success}</div>
            <p className="vp-modal-success__hint">The pin has been removed from your account and assigned to the recipient.</p>
            <button className="vp-modal-btn vp-modal-btn--primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          /* ── Form State ── */
          <form onSubmit={handleSubmit} className="vp-modal-form">
            <div className="vp-modal-field">
              <label className="vp-modal-field__label">Recipient's Email Address</label>
              <input
                ref={inputRef}
                type="email"
                className={`vp-modal-field__input ${status.error ? 'vp-modal-field__input--error' : ''}`}
                placeholder="e.g. recipient@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus({ loading: false, error: '', success: '' }); }}
                disabled={status.loading}
                required
              />
              {status.error && <p className="vp-modal-field__error">{status.error}</p>}
            </div>

            <div className="vp-modal-rules">
              <p>⚠️ Important:</p>
              <ul>
                <li>The recipient must be a registered user.</li>
                <li>You cannot send a pin to yourself.</li>
                <li>This action is <strong>irreversible</strong>.</li>
              </ul>
            </div>

            <div className="vp-modal-actions">
              <button type="button" className="vp-modal-btn vp-modal-btn--ghost" onClick={onClose} disabled={status.loading}>
                Cancel
              </button>
              <button
                type="submit"
                className={`vp-modal-btn vp-modal-btn--primary ${status.loading ? 'vp-modal-btn--loading' : ''}`}
                disabled={status.loading || !emailValid}
              >
                {status.loading ? <span className="vp-modal-spinner" /> : '🔗 Send Pin'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};


/* ─────────────── Main Component ─────────────── */
const ViewPinCode = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [copied, setCopied] = useState(null);
  const [sharePin, setSharePin] = useState(null); 

  const fetchData = () => {
    setLoading(true);
    apiFetch(`/api/pin-codes?page=${page}`)
      .then(res => res.json())
      .then(res => { 
        setPins(res.pins || []); 
        setTotalPages(res.totalPages || 1);
        setTotalCount(res.totalCount || 0);
        setLoading(false); 
      })
      .catch(() => setLoading(false));
  };

  useEffect(fetchData, [page]);

  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleShareSuccess = () => {
    setSharePin(null);
    fetchData(); 
  };

  const available = pins.filter(p => p.status === 'Available');
  const used = pins.filter(p => p.status === 'Used');
  const shared = pins.filter(p => p.status === 'Shared');
  const expired = pins.filter(p => p.status === 'Expired');

  if (loading) {
    return (
      <div className="vp-page">
        <Preloader message="Fetching your E-Pins..." />
      </div>
    );
  }

  return (
    <div className="vp-page">
      {/* Share Modal */}
      {sharePin && (
        <SharePinModal
          pin={sharePin}
          onClose={() => setSharePin(null)}
          onSuccess={handleShareSuccess}
        />
      )}

      <div className="vp-header">
        <div>
          <h1 className="vp-header__title">My E-Pins</h1>
          <p className="vp-header__sub">Your digital vouchers for account activation</p>
        </div>
      </div>

      {/* Stats */}
      <div className="vp-stats">
        <div className="vp-stat vp-stat--total">
          <span className="vp-stat__num">{totalCount}</span>
          <span className="vp-stat__label">Total</span>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="vp-empty">
          <div className="vp-empty__icon">🔑</div>
          <h3>No E-Pins Yet</h3>
          <p>Request pins from the "Request E-Pins" page. Once approved, they'll appear here.</p>
        </div>
      ) : (
        <>
          {/* Available Pins */}
          {available.length > 0 && (
            <div className="vp-section">
              <h2 className="vp-section__title">🟢 Available Pins (Page {page})</h2>
              <div className="vp-grid">
                {available.map(pin => (
                  <div key={pin.id} className="vp-card vp-card--avail">
                    <div className="vp-card__badge">{pin.status}</div>
                    <code className="vp-card__code">{pin.code}</code>
                    <div className="vp-card__actions">
                      <button
                        className={`vp-card__copy ${copied === pin.id ? 'vp-card__copy--done' : ''}`}
                        onClick={() => handleCopy(pin.code, pin.id)}
                      >
                        {copied === pin.id ? '✓ Copied!' : '📋 Copy'}
                      </button>
                      <button
                        className="vp-card__share"
                        onClick={() => setSharePin(pin)}
                        title="Share this pin with another user"
                      >
                        🔗 Share
                      </button>
                    </div>
                    <div className="vp-card__meta">
                      Use this pin in "Join a User" to activate an account.
                      {pin.expiresAt && (
                        <div style={{ marginTop: 4, color: 'var(--awp-warning)', fontWeight: 'bold' }}>
                          Expires: {pin.expiresAt}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Used Pins */}
          {used.length > 0 && (
            <div className="vp-section">
              <h2 className="vp-section__title">⚫ Used Pins</h2>
              <div className="vp-grid">
                {used.map(pin => (
                  <div key={pin.id} className="vp-card vp-card--used">
                    <div className="vp-card__badge vp-card__badge--used">Used</div>
                    <code className="vp-card__code">{pin.code}</code>
                    {pin.usedBy && (
                      <div className="vp-card__used-info">
                        <span>Activated: <strong>{pin.usedBy}</strong></span>
                        {pin.usedAt && <span className="vp-card__date">{pin.usedAt}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shared Pins */}
          {shared.length > 0 && (
            <div className="vp-section">
              <h2 className="vp-section__title">🔗 Shared Pins</h2>
              <div className="vp-grid">
                {shared.map(pin => (
                  <div key={pin.id} className="vp-card vp-card--shared">
                    <div className="vp-card__badge vp-card__badge--shared">Shared</div>
                    <code className="vp-card__code vp-card__code--shared">{pin.code}</code>
                    <div className="vp-card__used-info">
                      <span>📤 Sent to: <strong>@{pin.sharedTo}</strong></span>
                      {pin.sharedAt && (
                        <span className="vp-card__date">📅 {pin.sharedAt}</span>
                      )}
                      <span style={{fontSize: 11, color: 'var(--awp-text-muted)', marginTop: 4}}>This pin was transferred from your account.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expired Pins */}
          {expired.length > 0 && (
            <div className="vp-section">
              <h2 className="vp-section__title">🔴 Expired Pins</h2>
              <div className="vp-grid">
                {expired.map(pin => (
                  <div key={pin.id} className="vp-card vp-card--expired">
                    <div className="vp-card__badge vp-card__badge--expired">Expired</div>
                    <code className="vp-card__code vp-card__code--expired">{pin.code}</code>
                    <div className="vp-card__used-info">
                      <span className="vp-card__date">⏰ Expired on: <strong>{pin.expiresAt}</strong></span>
                      <span style={{ fontSize: 11, color: 'var(--awp-text-muted)', marginTop: 4 }}>This pin is no longer valid and cannot be used.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Pagination 
            currentPage={page} 
            totalPages={totalPages} 
            onPageChange={setPage} 
          />
        </>
      )}
    </div>
  );
};

export default ViewPinCode;